import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  Divider,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  makeStyles,
  Button,
  TextField,
} from "@material-ui/core";
import PropTypes from "prop-types";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getRestrictiveValue, validateFile,filterFutureTime } from "../../utils/utils";
import { RESTRICTIVE_VALIDATION_RULES } from "../../pages/IEPFReport/validationRules";
import { HINT_FORM_TEXT } from "../../constants";

const useStyles = makeStyles((theme) => ({
  categoryModal: {
    minWidth: 400,
    overflow: "hidden",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: theme.shadows[5],
    padding: 0,
    maxWidth: "90vw",
    outline: "none",
    minHeight: "75vh",
    overflowY: "auto",
  },
  header: {
    padding: theme.spacing(1, 3),
    minHeight: theme.spacing(8),
  },
  modalContainer: {
    overflow: "auto",
  },
  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.5rem",
    color: theme.palette.text.primary,
  },
  formGroup: {
    marginBottom: theme.spacing(3),
  },
  fileInput: {
    display: "none",
  },
  fileInputLabel: {
    display: "flex",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    cursor: "pointer",
    transition: theme.transitions.create(["border-color"]),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  fileInputText: {
    padding: theme.spacing(2),
    flexGrow: 1,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
  },
  browseButton: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    padding: theme.spacing(2, 4),
    borderRadius: 0,
    textTransform: "none",
    fontWeight: theme.typography.fontWeightMedium,
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
    },
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  previewBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(1),
    border: "1px solid #ccc",
    borderRadius: 4,
    width: "120px",
    height: "120px",
    position: "relative",
    backgroundColor: "#fff",
    "&:hover": {
      boxShadow: theme.shadows[2],
    },
  },
  previewIcon: {
    fontSize: "2rem",
    color: "#054f96",
  },
  previewFileName: {
    fontSize: "0.75rem",
    textAlign: "center",
    wordBreak: "break-word",
    maxWidth: "100%",
  },
  deletePreviewButton: {
    position: "absolute",
    top: -15,
    right: -15,
    padding: 2,
    backgroundColor: "#e0e0e0",
    "&:hover": {
      backgroundColor: "#d0d0d0",
    },
  },
  dateInput: {
    width: "100%",
  },
  btn: {
    borderRadius: "10px",
    padding: theme.spacing(1, 4),
    color: "#FFFFFF",
  },
  cancelBtn: {
    background: "linear-gradient(90.49deg, #373738 -0.38%, #565656 99.9%)",
    "&:hover": {
      background: "linear-gradient(90.49deg, #2c2c2d -0.38%, #484848 99.9%)",
    },
    border: "1px",
  },
  saveBtn: {
    backgroundColor: "#00205b",
    color: "white",
    "&:hover": {
      backgroundColor: "#001845",
    },
  },
}));

const CustomDateTimeInput = React.forwardRef(
  ({ value, onClick, className }, ref) => (
    <TextField
      ref={ref}
      variant="outlined"
      fullWidth
      value={value || ""}
      onClick={onClick}
      placeholder="DD/MM/YYYY - HH:MM"
      InputProps={{
        readOnly: true,
      }}
      className={className} // <- This ensures it inherits styles like width: 100%
    />
  )
);

CustomDateTimeInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  // ref:PropTypes.any
};

const AddSRNDialogBox = ({
  openDialogBox,
  onClose,
  formData,
  setFormData,
  OnSave,
  isUploading,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]; // Get only the first file

    if (!file) return;

    try {
      validateFile(file);
      setFormData((prev) => ({
        ...prev,
        files: [file], // Replace the entire array with single file
      }));
      setUploadError("");
      setUploadSuccess("File selected successfully");
    } catch (err) {
      setUploadError(err.message);
      setUploadSuccess("");
    }

    e.target.value = ""; // Reset input
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, date }));
  };

  const handleSrnChange = (e) => {
    const cleaned = getRestrictiveValue(
      e.target.value,
      RESTRICTIVE_VALIDATION_RULES.SRN
    );
    setFormData((prev) => ({ ...prev, srn: cleaned }));
  };

  const handleSave = async () => {
    const { srn, files, date } = formData;

    if (files.length === 0) {
      setUploadError("Please select a file.");
      setUploadSuccess("");
      return;
    }

    if (!date) {
      setUploadError("Please select date");
      setUploadSuccess("");
      return;
    }

    if (!srn) {
      setUploadError("Please enter SRN number.");
      setUploadSuccess("");
      return;
    }

    // Upload happens here on save confirmation
    await OnSave(formData);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={openDialogBox}
      onClose={handleCancel}
      className={classes.modalContainer}
    >
      <div className={classes.paper}>
        <div className={classes.header}>
          <Grid container className={classes.containerHeader}>
            <Grid item>
              <Typography className={classes.title} variant="h4">
                Add SRN
              </Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleCancel}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </div>

        <Divider />

        <DialogContent className={classes.categoryModal}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={7}>
              <div className={classes.formGroup}>
                <Typography variant="subtitle1">Courier Image *</Typography>
                <input
                  accept="image/*"
                  className={classes.fileInput}
                  id="courier-image-upload"
                  type="file"
                  ref={fileInputRef}
                  name="files"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="courier-image-upload"
                  className={classes.fileInputLabel}
                >
                  <div className={classes.fileInputText}>
                    {formData.files.length > 0
                      ? `${formData.files[0].name}` // Show the single file name
                      : "Choose file..."}
                  </div>

                  <Button component="span" className={classes.browseButton}>
                    Browse
                  </Button>
                </label>

                {formData.files.length > 0 && (
                  <div className={classes.previewContainer}>
                    {formData.files.map((file, index) => (
                      <div
                        key={`${file.name}+${file.size}`}
                        className={classes.previewBox}
                      >
                        <FileCopyIcon className={classes.previewIcon} />
                        <Typography className={classes.previewFileName}>
                          {file.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ fontSize: "0.6rem" }}
                        >
                          {formatFileSize(file.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          className={classes.deletePreviewButton}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={5}>
              <div className={classes.formGroup}>
                <Typography variant="subtitle1">Date *</Typography>
                <DatePicker
                  selected={formData.date}
                  onChange={handleDateChange}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={1}
                  maxDate={new Date()}
                  dateFormat="dd/MM/yyyy - HH:mm"
                  filterTime={filterFutureTime}
                  customInput={
                    <CustomDateTimeInput className={classes.dateInput} />
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.formGroup}>
                <Typography variant="subtitle1">SRN Number *</Typography>
                <TextField
                  fullWidth
                  required
                  placeholder={HINT_FORM_TEXT.SRN}
                  value={formData.srn}
                  onChange={handleSrnChange}
                  variant="outlined"
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        {uploadError && (
          <Typography style={{ color: "red", padding: "0.5rem 2rem" }}>
            {uploadError}
          </Typography>
        )}
        {uploadSuccess && (
          <Typography style={{ color: "green", padding: "0.5rem 2rem" }}>
            {uploadSuccess}
          </Typography>
        )}

        <DialogActions>
          <div style={{ padding: "12px", paddingTop: 0 }}>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  onClick={handleCancel}
                  className={`${classes.btn} ${classes.cancelBtn}`}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={handleSave}
                  disabled={isUploading}
                  className={`${classes.btn} ${classes.saveBtn}`}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
};

AddSRNDialogBox.propTypes = {
  openDialogBox: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  OnSave: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
};

export default AddSRNDialogBox;
